import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  CdsCheckingAccountDropdownComponent,
  CdsCoaAutocompleteComponent,
  CdsCostCenterAutocompleteComponent,
} from '@celerofinancas/common-domain';
import { CdsInputWrapperComponent, CdsInputsModule } from '@celerofinancas/design-system';
import { BANK, Bank, Banks } from '@celerofinancas/core-ts';
import { ActiveCoaState, CheckingAccountState, CompanyState, LoadActiveCoaAction } from '@celerofinancas/state-management';
import { Filter } from '@celerofinancas/ui-cash-flow';
import { ModalRef } from '@celerofinancas/ui-modals';
import { Store } from '@ngxs/store';
import { addDays, addMonths, endOfMonth, endOfWeek, format, startOfMonth, startOfWeek, subDays } from 'date-fns';
import { merge } from 'rxjs';

/**
 * Record status — three mutually exclusive options that become a single flag.
 */
type FinancialRecordStatusFilter = 'is_done' | 'is_predicted' | 'is_late';

/**
 * Tab filters, in the shape `CompanyService` expects.
 */
export type StatementFilters = Filter & {
  /**
   * Keeps only settled records.
   */
  is_done?: boolean;

  /**
   * Keeps only records still to settle.
   */
  is_predicted?: boolean;

  /**
   * Keeps only overdue and unpaid records.
   */
  is_late?: boolean;

  /**
   * Keeps only records with no sector/project.
   */
  cost_center_null?: boolean;

  /**
   * Payment method code, from the core-ts `PAYMENTS_METHODS` enum.
   */
  payment_methods?: number;
};

/**
 * What the modal returns when closing. `null` for "Limpar filtros".
 */
export interface FiltersModalResult {
  /**
   * Filters to apply, or `null` to clear everything.
   */
  filters: StatementFilters | null;
}

/**
 * Label of the period option that leaves the date range under manual control.
 */
const CUSTOM_PERIOD = 'Personalizado';

/**
 * Filters modal of the "Todos os registros" tab.
 *
 * A port of `filters` from `paginated-financial-list` in
 * `serasa-cl-webcomponents` — rule 4 of card
 * [86e2v9afr](https://app.clickup.com/t/86e2v9afr) asks for exactly *"the same
 * filters that exist in the Serasa custom layer"*. The ten groups are the same and
 * in the same order: date type, period (with the nine presets), custom range,
 * record type, category, sector/project, payment method, record status and the
 * document/sector triage.
 *
 * ## Two behavioural differences, both deliberate
 *
 * 1. **`classification_pk` goes out as a `pk`, not as an object.**
 *    `cds-coa-autocomplete` does not declare `keyField`, so `cds-autocomplete`
 *    writes the **whole account** into the control (`{ pk, index, name, … }`).
 *    Serasa unwraps `cost_center_pk` and forgets this one — the value travels to
 *    the query and `HttpParams` serialises it as `[object Object]`, a filter no
 *    API matches. The package's own `Filter` contract declares
 *    `classification_pk?: string`, which is what we send.
 *
 * 2. **The active chart of accounts is loaded here.** The category autocomplete
 *    needs the active plan's `pk` to build the URL
 *    (`/v2/coa/autocomplete/?pk=…`). In Serasa some earlier screen has already
 *    filled `ActiveCoaState`; here the statement may be the first screen opened,
 *    so the modal dispatches `LoadActiveCoaAction` when the state is empty.
 *    Without it the field opens and never suggests anything — with no console
 *    error, because the request goes out with an empty `pk` and comes back 200
 *    with an empty list.
 *
 * ## "Aplicar" starts disabled
 *
 * While nothing changes in the form there is nothing to apply, and enabling the
 * button would invite a reload identical to what is already on screen. Same rule
 * as Serasa.
 */
@Component({
  selector: 'statement-filters',
  standalone: true,
  imports: [ReactiveFormsModule, CdsInputsModule, CdsCoaAutocompleteComponent, CdsInputWrapperComponent, CdsCostCenterAutocompleteComponent, CdsCheckingAccountDropdownComponent],
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiltersComponent implements OnInit {
  /**
   * Modal reference, which carries the filters already applied and closes with the
   * result.
   */
  private readonly modalRef = inject<ModalRef<FiltersModalResult, { filters?: StatementFilters }>>(ModalRef);

  /**
   * NGXS store, read for the active company and the active chart of accounts.
   */
  private readonly store = inject(Store);

  /**
   * Destroy reference, which unsubscribes the form watchers with the component.
   */
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Active chart of accounts — feeds the category autocomplete.
   */
  public readonly activeCoaPk = toSignal(this.store.select(ActiveCoaState.getActiveCoaPk), {
    initialValue: '',
  });

  /**
   * Active company — the sector/project autocomplete filters by it.
   */
  public readonly companyPk = signal('');

  /**
   * The company's checking accounts, the same list the "Realizados" filter modal reads.
   */
  private readonly checkingAccounts = toSignal(this.store.select(CheckingAccountState.getActiveState), {
    initialValue: [],
  });

  /**
   * Banks the company actually has an account in. The bank selector lists every bank in
   * the country otherwise, and picking one without an account would only lead to an
   * empty "Conta".
   */
  public readonly banksWithAccounts = computed(() => [
    ...new Set(this.checkingAccounts().map((account) => account.bank_id)),
  ]);

  /**
   * Bank currently chosen, which decides the accounts "Conta" offers.
   */
  public readonly selectedBankId = signal<string | null>(this.receivedFilters.bank_id ?? null);

  /**
   * The chosen bank in the shape the selector takes to show it: the core-ts `Bank` record
   * plus the `bank_id` it keys by. Resolved through `Banks.get`, because the selector
   * types its input as a full `Bank` and a bare `{ bank_id }` does not satisfy it.
   */
  public readonly selectedBank = computed((): Bank & { bank_id?: string } => {
    const bankId = this.selectedBankId();

    // With no bank the selector gets an empty record, which it shows as the "Todos"
    // placeholder. `bank_id` arrives from the API as a string; `BANK` is the enum of the
    // same codes, hence the cast.
    return bankId ? { ...Banks.get(bankId as BANK), bank_id: bankId } : ({} as Bank);
  });

  /**
   * Accounts of the chosen bank — R1 asks for the "Realizados" behaviour, where "Conta"
   * only opens up from the bank selected.
   */
  public readonly accountsOfSelectedBank = computed(() => {
    const bankId = this.selectedBankId();

    return bankId ? this.checkingAccounts().filter((account) => account.bank_id === bankId) : [];
  });

  /**
   * Record status options, rendered as a single-choice radio group.
   */
  public readonly financialRecordStatusOptions: { label: string; value: FinancialRecordStatusFilter }[] = [
    { label: 'Realizado', value: 'is_done' },
    { label: 'Previsto', value: 'is_predicted' },
    { label: 'Atrasado', value: 'is_late' },
  ];

  /**
   * The nine period presets, plus the manual option, in Serasa's order.
   */
  public readonly periodOptions: string[] = [
    CUSTOM_PERIOD,
    'Ontem',
    'Hoje',
    'Amanhã',
    'Semana Passada',
    'Essa Semana',
    'Próxima Semana',
    'Mês Passado',
    'Esse Mês',
    'Próximo Mês',
  ];

  /**
   * Signal holding the period preset currently selected.
   */
  public readonly period = signal<string>(CUSTOM_PERIOD);

  /**
   * Whether the user has touched the form — what enables the submit button.
   */
  private readonly hasChanged = signal(false);

  /**
   * Guard that tells a user-picked date apart from a date derived from a preset —
   * see {@link setPeriod}.
   */
  private isApplyingPreset = false;

  /**
   * Whether the submit button is disabled, derived from {@link hasChanged}.
   */
  public readonly isDisabled = computed(() => !this.hasChanged());

  /**
   * Reactive form backing the ten filter groups.
   */
  public readonly form = new FormGroup({
    date_type: new FormControl<string | null>(this.receivedFilters.date_type ?? null),
    financial_record_status: new FormControl<FinancialRecordStatusFilter | null>(this.initialStatus()),
    financial_record_type: new FormControl<string | null>(this.receivedFilters.financial_record_type ?? null),
    /**
     * Category: the raw `pk` goes back into the control, and the field reopens
     * **blank**. `cds-coa-autocomplete` can only label accounts present in the
     * suggestion list, and the list is only fetched once the user types — there is
     * no way to resolve the name of a lone `pk` without a read-by-`pk` endpoint,
     * which the API does not have. The chip in the header is what tells the user
     * the filter is active; the value stays in the control and is sent again if
     * they submit without touching the field.
     */
    classification_pk: new FormControl<unknown>(this.receivedFilters.classification_pk ?? null),
    /**
     * Sector/Project goes in as an **object** `{ pk }`, not as a string.
     *
     * `cost-center-autocomplete` resolves the name from the `pk` against the
     * `CostCenterState` list — but it only takes that path when the control value
     * is an object (it reads `value?.pk`). With the raw string the field reopens
     * empty even with the filter active, which was the observed behaviour before
     * this conversion. `extractPk` unwraps it on the way back.
     */
    cost_center_pk: new FormControl<unknown>(
      this.receivedFilters.cost_center_pk ? { pk: this.receivedFilters.cost_center_pk } : null,
    ),
    payment_methods: new FormControl<number | null>(this.receivedFilters.payment_methods ?? null),
    has_document: new FormControl<boolean | null>(this.receivedFilters.has_document ?? null),
    cost_center_null: new FormControl<boolean | null>(this.receivedFilters.cost_center_null ?? null),
    date_range_start: new FormControl<string | null>(this.receivedFilters.date_range_start ?? null),
    date_range_end: new FormControl<string | null>(this.receivedFilters.date_range_end ?? null),
    /**
     * Bank and account, under the names the list endpoint takes (`Filter` in
     * `ui-cash-flow`): `bank_id` and `checking_accounts`. The account goes as its `pk`,
     * which is what `keyField: 'pk'` makes the dropdown write.
     *
     * "Conta" starts disabled when no bank came in with the filters, for the same reason
     * {@link selectBank} toggles it.
     */
    bank_id: new FormControl<string | null>(this.receivedFilters.bank_id ?? null),
    checking_accounts: new FormControl<string | null>({
      value: this.receivedFilters.checking_accounts ?? null,
      disabled: !this.receivedFilters.bank_id,
    }),
  });

  /**
   * Filters the caller passed in, or an empty object when the modal opens clean.
   */
  private get receivedFilters(): StatementFilters {
    return this.modalRef.data?.filters ?? {};
  }

  /**
   * Resolves the active company, requests the chart of accounts when it is missing
   * and starts watching the form.
   */
  public ngOnInit(): void {
    const companyPk = this.store.selectSnapshot(CompanyState.getActiveCompanyPk);
    this.companyPk.set(companyPk ?? '');

    if (!this.activeCoaPk() && companyPk) {
      this.store.dispatch(new LoadActiveCoaAction(companyPk));
    }

    // A write is only a change when the form actually holds something different from what
    // it opened with. `cds-checking-account-dropdown` re-emits its control while it sets
    // itself up, and counting that as the user touching the form enabled "Aplicar filtros"
    // before anyone had done anything.
    const openedWith = JSON.stringify(this.form.getRawValue());

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (JSON.stringify(this.form.getRawValue()) !== openedWith) {
        this.hasChanged.set(true);
      }
    });

    // Touching a date by hand undoes the preset: the label "Esse Mês" with dates
    // from another month would lie about what is going to be filtered.
    merge(this.form.controls.date_range_start.valueChanges, this.form.controls.date_range_end.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.isApplyingPreset) {
          this.period.set(CUSTOM_PERIOD);
        }
      });
  }

  /**
   * Switches the period and derives the date range.
   *
   * Both date fields stay **always visible**, showing the interval the preset
   * chose; Serasa hides them outside "Personalizado". The difference is display,
   * not filtering — the dates are what goes to the query either way. They stay
   * visible because a preset on its own is opaque: "Semana Passada" does not say
   * which days it covered, and that is the information needed to check the list.
   * As a bonus, the modal height does not change when the period is picked.
   *
   * `emitEvent` stays on because `valueChanges` is how `cds-datepicker` refreshes
   * what shows in the field — hence the `isApplyingPreset` guard, so this write is
   * not mistaken for a user edit.
   *
   * @param value preset label picked in the dropdown
   */
  public setPeriod(value: string): void {
    this.period.set(value);

    const range = this.getDateRange(value);

    if (!range) {
      // "Personalizado" leaves the dates alone: it keeps whatever was there.
      this.hasChanged.set(true);
      return;
    }

    this.isApplyingPreset = true;
    this.form.patchValue({
      date_range_start: format(range.start, 'yyyy-MM-dd'),
      date_range_end: format(range.end, 'yyyy-MM-dd'),
    });
    this.isApplyingPreset = false;
  }

  /**
   * Toggles the document filter.
   *
   * Both switches ("Com documento" and "Sem documento") write to the **same**
   * `has_document` control — clicking the one already on goes back to null, which
   * means "either". That is why they are not two independent checkboxes: turning
   * both on would ask for records with and without a document, which is the
   * absence of a filter.
   *
   * @param value which switch was clicked: `true` for "Com documento", `false` for "Sem documento"
   */
  public onCheckboxChange(value: boolean): void {
    const current = this.form.get('has_document')?.value;

    this.form.get('has_document')?.setValue(current === value ? null : value, { emitEvent: false });
    this.hasChanged.set(true);
  }

  /**
   * Takes the bank picked in the selector and opens "Conta" to its accounts.
   *
   * The account chosen before is dropped: it belonged to the previous bank, and keeping
   * it would send a bank and an account that do not go together. With no bank, "Conta"
   * goes back to disabled — the same "enables from the bank" rule the "Realizados"
   * modal follows.
   *
   * @param bank what the selector emits; the package fills `bank_id` or, for some banks,
   * only `code`, and the "Realizados" modal reads them in that order too
   */
  public selectBank(bank: { bank_id?: string; code?: string } | null): void {
    const bankId = bank?.bank_id ?? bank?.code ?? null;
    const account = this.form.controls.checking_accounts;

    this.selectedBankId.set(bankId);
    this.form.controls.bank_id.setValue(bankId);
    account.setValue(null);

    if (bankId) {
      account.enable();
    } else {
      account.disable();
    }
  }

  /**
   * Closes returning `null`, which the caller reads as "clear everything".
   */
  public clearFilters(): void {
    this.form.reset();
    this.modalRef.close({ filters: null });
  }

  /**
   * Closes returning the filled filters, dropping every empty value.
   *
   * @param event submit event, whose default reload is prevented
   */
  public submitForm(event: Event): void {
    event.preventDefault();

    // "Sem setor/projeto" and a chosen sector are contradictory requests; the
    // switch is the more specific of the two, so it wins.
    if (this.form.get('cost_center_null')?.value) {
      this.form.get('cost_center_pk')?.setValue(null, { emitEvent: false });
    }

    const { financial_record_status, ...values } = this.form.getRawValue();

    const filters: Record<string, unknown> = {
      ...values,
      ...(financial_record_status ? { [financial_record_status]: true } : {}),
      classification_pk: this.extractPk(values.classification_pk),
      cost_center_pk: this.extractPk(values.cost_center_pk),
      // `false` here does not mean "only the ones with a sector" — it is the
      // absence of the filter. The switch turned off has to leave the query, or the
      // API receives `cost_center_null=false` and returns the opposite of what the
      // screen shows.
      cost_center_null: values.cost_center_null ? true : null,
    };

    for (const key of Object.keys(filters)) {
      if (filters[key] === null || filters[key] === undefined || filters[key] === '') {
        delete filters[key];
      }
    }

    this.modalRef.close({ filters: filters as StatementFilters });
  }

  /**
   * Initial radio state, derived from the flags already applied. Only one can be
   * on — the screen never sends two.
   *
   * @returns the status flag already applied, or `null` when none is
   */
  private initialStatus(): FinancialRecordStatusFilter | null {
    const filters = this.receivedFilters;

    return this.financialRecordStatusOptions.find(({ value }) => filters[value])?.value ?? null;
  }

  /**
   * Accepts both the bare `pk` (filter already applied) and the autocomplete object.
   *
   * @param value control value, either a `pk` string or an object carrying one
   * @returns the `pk`, or `null` when there is none to send
   */
  private extractPk(value: unknown): string | null {
    if (typeof value === 'string') {
      return value === '' ? null : value;
    }

    if (value && typeof value === 'object' && 'pk' in value) {
      const pk = (value as { pk: unknown }).pk;

      // `cds-coa-autocomplete` writes `{ pk: 'NULL' }` when the user clears the
      // field with the "x" — a package sentinel, not a pk.
      return typeof pk === 'string' && pk !== 'NULL' ? pk : null;
    }

    return null;
  }

  /**
   * Date range of a period preset.
   *
   * @param label preset label, as rendered in the dropdown
   * @returns the interval the preset covers, or `null` for the manual option
   */
  private getDateRange(label: string): { start: Date; end: Date } | null {
    const today = new Date();

    switch (label) {
      case 'Ontem': {
        const day = subDays(today, 1);
        return { start: day, end: day };
      }
      case 'Hoje':
        return { start: today, end: today };
      case 'Amanhã': {
        const day = addDays(today, 1);
        return { start: day, end: day };
      }
      case 'Semana Passada':
        return { start: subDays(startOfWeek(today), 7), end: subDays(endOfWeek(today), 7) };
      case 'Essa Semana':
        return { start: startOfWeek(today), end: endOfWeek(today) };
      case 'Próxima Semana':
        return { start: addDays(startOfWeek(today), 7), end: addDays(endOfWeek(today), 7) };
      case 'Mês Passado': {
        const reference = addMonths(today, -1);
        return { start: startOfMonth(reference), end: endOfMonth(reference) };
      }
      case 'Esse Mês':
        return { start: startOfMonth(today), end: endOfMonth(today) };
      case 'Próximo Mês': {
        const reference = addMonths(today, 1);
        return { start: startOfMonth(reference), end: endOfMonth(reference) };
      }
      default:
        return null;
    }
  }
}
